import React, { FC, useState } from "react";
import {
  Box,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import axiosInstant from "../../api";
import { updateSelectionFilmWindow } from "../../api/endpoints";
import { Button } from "../Button/Button";
import {
  ExpertRangeCalendar,
  DayRange,
} from "../FilmingScheduling/ExpertRangeCalendar";
import {
  FILM_DAYS_ERRORS,
  FilmWindow,
  formatFilmWindow,
  getFilmDays,
  hasFilmWindow,
  getFilmDaysCount,
  toDay,
} from "../../utils/filmWindow";
import { ConfirmationDialog } from "../ConfirmationDialog/ConfirmationDialog";

interface Props {
  orderId?: number | null;
  selectionId?: number | null;
  /** Needed only for the day write, which goes to the project endpoint. */
  brandId?: number | null;
  projectId?: number | null;
  filmWindow?: FilmWindow | null;
  canEdit: boolean;
  disabled?: boolean;
  onSaved?: (next: FilmWindow, sideEffects: boolean) => void;
  optedInCount?: number;
  rankedCount?: number;
  isCommitted?: boolean;
  projectCount?: number;
}

/**
 * FRFD — Film Range OR Film Date. The internal team decides which it is giving
 * the experts: a filming RANGE (they answer with a preferred day) or a single
 * hard FILM DATE (they answer yes/no, no date input at all).
 *
 * The mode is DERIVED, never stored: a hard date IS est_film_start ===
 * est_film_end === the selection's one scheduled film day.
 */
type FilmWindowMode = "range" | "exact";

const DAY_KEY = "YYYY-MM-DD";

export const FilmWindowField: FC<Props> = ({
  orderId,
  selectionId,
  filmWindow,
  canEdit,
  disabled,
  onSaved,
  brandId,
  projectId,
  optedInCount = 0,
  rankedCount = 0,
  isCommitted = false,
  projectCount = 0,
}) => {
  // ---- saved state, and the mode derived from it -------------------------
  const savedStart = toDay(filmWindow?.est_film_start);
  const savedEnd = toDay(filmWindow?.est_film_end);
  const scheduledDays = getFilmDays(filmWindow);
  const scheduledDayCount = scheduledDays.length;
  const filmDaysCount = getFilmDaysCount(filmWindow);
  // D6: the exact-date option is only ever offered for a one-day shoot.
  // Collapsing a 3-day shoot to a single day would destroy two day rows and
  // void its SOWs on one toggle click, so multi-day selections stay pure range
  // — the toggle is hidden AND the mode is pinned below.
  const exactAvailable = filmDaysCount <= 1;
  // All three clauses of the definition: the window is one day AND that day is
  // the scheduled shoot. A one-day window with no film day row is NOT a hard
  // date — it is an unscheduled one-day range, and treating it as exact would
  // suppress the confirmation dialog on the save that first sets the date.
  // A film date is set when the shoot's days are scheduled. The window is NOT
  // collapsed to that day: the day only has to fall INSIDE it (validateFilmDays),
  // and leaving the window alone is what keeps the window endpoint's
  // preferred-date voiding from firing on an already opted-in roster.
  const savedIsExact = exactAvailable && scheduledDayCount === 1;
  const scheduledDay = scheduledDayCount === 1 ? toDay(scheduledDays[0]) : null;

  // A hard date does NOT require a collapsed window (savedIsExact above only
  // asks for ONE scheduled day), so exact mode has to open on that DAY, never
  // on the window start. Seeding from the window would preselect Mar 1 for a
  // Mar 1-20 window whose shoot is on Mar 10, and one click of Save Date would
  // reschedule the shoot to Mar 1 -- resetting every date confirmation and
  // voiding the SOWs. handleModeChange already seeds from scheduledDay; the
  // initial state and every re-open go through here for the same reason.
  const savedRange = (): DayRange =>
    savedIsExact && scheduledDay
      ? [scheduledDay, scheduledDay]
      : [savedStart, savedEnd];

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [range, setRange] = useState<DayRange>(savedRange);
  const [pickedMode, setPickedMode] = useState<FilmWindowMode>(
    savedIsExact ? "exact" : "range"
  );
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Never trust the toggle alone: the mode is pinned to range whenever an exact
  // date is not on offer (D6), so a stale "exact" can never reach the payload.
  const mode: FilmWindowMode = exactAvailable ? pickedMode : "range";

  // The eyebrow says "Filming Date" for a saved hard date, so the value under
  // it has to BE that date. formatFilmWindow renders the window, which for the
  // uncollapsed case reads "FILMING DATE / Mar 1 - 20, 2026".
  const savedDisplay =
    savedIsExact && scheduledDay
      ? scheduledDay.format("MMM D, YYYY")
      : formatFilmWindow(filmWindow);
  const label = savedDisplay || "Set filming range or date";

  // In exact mode the API requires the day to fall INSIDE the film window
  // (validateFilmDays -> FilmDateOutsideFilmWindow), so bound the calendar to
  // it rather than letting the pick fail on save. With no window yet this save
  // creates one, so only "not in the past" applies.
  const today = dayjs();
  // A window that has already elapsed admits no future day: floor-at-today
  // against a past ceiling disables every square in the calendar with no
  // explanation. The API only asks that the day fall inside the window, so
  // there the floor is the window start and back-dating the shoot is allowed.
  const savedWindowIsPast = !!savedEnd && savedEnd.isBefore(today, "day");
  const exactMinDate = (() => {
    if (mode !== "exact") return today;
    if (savedWindowIsPast) return savedStart || undefined;
    if (savedStart && savedStart.isAfter(today, "day")) return savedStart;
    return today;
  })();
  const exactMaxDate =
    mode === "exact" && savedEnd ? savedEnd : undefined;
  // The calendar caption states the CONSTRAINT (which days are legal). The
  // consequence of the mode lives with the toggle, above.
  const calendarCaption =
    mode !== "exact"
      ? undefined
      : hasFilmWindow(filmWindow)
        ? `Inside ${formatFilmWindow(filmWindow)}`
        : "This also sets the filming window to that day.";
  const interactive = canEdit && !disabled && !!orderId && !!selectionId;

  const [nextStart, nextEnd] = range;

  // ---- what this save writes ---------------------------------------------
  // T1: film_dates is sent ONLY when this save actually decides the days — in
  // exact mode (the one day IS the decision) or on the explicit exit out of a
  // saved hard date back to a range (the day is deliberately given up). An
  // ordinary range save omits the key entirely so the API's daysProvided stays
  // false: sending film_dates: [] there would make it drop a perfectly valid
  // scheduled day, unwind the selection, and void detached DocuSign SOWs with
  // no confirmation dialog in front of it.
  const exitingExact = savedIsExact && mode === "range";
  const daysProvided = mode === "exact" || exitingExact;
  const nextDays: string[] =
    mode === "exact" && nextStart ? [nextStart.format(DAY_KEY)] : [];

  // ---- consequences: mirrors the API's selective invalidation ------------
  // A STORED scheduled day that no longer fits the new window is dropped by the
  // API, which unwinds a committed shoot. Computed from the stored days — never
  // from the days this save is about to write.
  const filmDateDropped =
    scheduledDayCount > 0 &&
    !!nextStart &&
    !!nextEnd &&
    scheduledDays.some((day) => {
      const d = toDay(day);
      return !!d && (d.isBefore(nextStart, "day") || d.isAfter(nextEnd, "day"));
    });

  // Must mirror the API's scheduleStands exactly. When the days travel in the
  // request body the DB still holds the OLD ones, so "does a shoot stand" has
  // to be answered from the incoming days, not the stored ones — otherwise a
  // one-day window makes almost every preferred day "outside" and the
  // roster-voiding branch matches practically everyone.
  const scheduleStands = daysProvided
    ? nextDays.length > 0
    : scheduledDayCount > 0 && !filmDateDropped;

  // The scheduled days go away either because they fell outside the new window
  // or because the explicit exit from exact mode clears them.
  const daysCleared = filmDateDropped || (exitingExact && scheduledDayCount > 0);

  // Whether this save unwinds a committed selection. The project endpoint that
  // writes the day returns the project row, NOT side-effect counters, so when
  // the window is left alone there is no server number to report -- and this
  // is the very predicate the confirmation dialog already promised it from.
  const movesProjectsBack = isCommitted && daysCleared;
  // The day write goes to the project endpoint, so with no brand or project it
  // cannot happen at all. brandId is derived from a non-RMN brand filter on the
  // page and can legitimately be undefined.
  const canWriteDays = brandId != null && projectId != null;

  const nextDayLabel = nextStart ? nextStart.format("MMM D, YYYY") : "";
  const savedDayLabel = savedStart ? savedStart.format("MMM D, YYYY") : "";
  const dateMoved =
    mode === "exact" &&
    !!nextStart &&
    scheduledDayCount > 0 &&
    !scheduledDays.includes(nextStart.format(DAY_KEY));
  // The experts' question changes iff the hard day differs from the day already
  // scheduled; with nothing scheduled yet, setting one always changes it.
  // Re-saving the same hard date is a no-op the API early-returns on, so it
  // must not raise a dialog claiming otherwise.
  const exactQuestionChanges =
    mode === "exact" && !!nextStart && (scheduledDayCount > 0 ? dateMoved : true);
  // Exiting exact mode by re-picking the same single day is not a range: the
  // window would not change, so the API would early-return while the dialog had
  // just promised a rollback. Caught in handleSaveClick.
  const degenerateExit =
    exitingExact &&
    !!nextStart &&
    !!nextEnd &&
    nextStart.isSame(nextEnd, "day");

  // Labels per mode. Flat IIFEs rather than nested ternaries (airbnb's
  // no-nested-ternary is an error here) so each mode's copy reads in one place.
  const saveButtonLabel = (() => {
    if (saving) return "Saving...";
    return mode === "exact" ? "Save Date" : "Save Range";
  })();
  const dialogTitle = (() => {
    if (mode !== "exact") return "Change the filming range?";
    return dateMoved ? "Move the filming date?" : "Set the filming date?";
  })();
  const dialogConfirmLabel = (() => {
    if (saving) return "Saving...";
    if (mode !== "exact") return "Change range";
    return dateMoved ? "Move date" : "Set date";
  })();

  // Once the client has ranked, the API only re-asks ranked experts.
  const keepsOptedInLabel = () => {
    if (rankedCount > 0) {
      return "Keeps every opted in expert. Ranked experts whose preferred day falls outside the new range are asked to pick a new one.";
    }
    return optedInCount === 1
      ? "Keeps the opted in expert. If their preferred day falls outside the new range, they are asked to pick a new one."
      : `Keeps the ${optedInCount} opted in experts. Any whose preferred day falls outside the new range are asked to pick a new one.`;
  };

  const changeConsequences = [
    ...(exactQuestionChanges && optedInCount > 0
      ? [
          optedInCount === 1
            ? `Asks the opted in expert one yes/no question: can they film on ${nextDayLabel}. They are not asked for a preferred date.`
            : `Asks the ${optedInCount} opted in experts one yes/no question: can they film on ${nextDayLabel}. They are not asked for a preferred date.`,
        ]
      : []),
    ...(exitingExact && scheduledDayCount > 0
      ? [
          `Gives up the set filming date of ${savedDayLabel} and goes back to asking experts for a preferred day inside the new range.`,
        ]
      : []),
    ...(optedInCount > 0 && !scheduleStands
      ? [
          keepsOptedInLabel(),
        ]
      : []),
    ...(daysCleared
      ? [
          mode === "exact"
            ? `Clears the ${
                scheduledDayCount === 1 ? "filming date" : "filming dates"
              } already scheduled and every expert's date confirmation, then asks them to confirm ${nextDayLabel} instead.`
            : `Clears the scheduled filming ${
                scheduledDayCount === 1 ? "date" : "dates"
              } and every expert's date confirmation, so the shoot is scheduled again inside the new range.`,
        ]
      : []),
    ...(movesProjectsBack
      ? [
          projectCount > 1
            ? `Moves this project and ${projectCount - 1} other${
                projectCount - 1 === 1 ? "" : "s"
              } on this selection back to Scheduling.`
            : "Moves this project back to Scheduling.",
          "Voids the SOWs already sent for this shoot, and the confirmed experts are notified.",
        ]
      : []),
    // Still in Scheduling: confirmed experts already hold SOWs for the dates
    // being dropped (sent at internal confirm), so those go too.
    ...(daysCleared && !movesProjectsBack
      ? [
          "Voids any SOWs already sent for this shoot and clears the internal confirmations; re-confirm each expert to send new ones. The confirmed experts are notified.",
        ]
      : []),
  ];
  const hasConsequences = changeConsequences.length > 0;

  // ---- open / mode toggle -------------------------------------------------
  // Re-derive on every open so a cancelled edit leaves nothing behind: the
  // popover always starts from what is actually saved on the selection.
  const openPicker = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setRange(savedRange());
    setPickedMode(savedIsExact ? "exact" : "range");
    setAnchor(e.currentTarget);
  };

  const handleModeChange = (
    _e: React.MouseEvent<HTMLElement>,
    next: FilmWindowMode | null
  ) => {
    // An exclusive ToggleButtonGroup hands back null when the active button is
    // re-clicked; there is no "no mode", so ignore it.
    if (!next || next === mode) return;
    if (next === "exact") {
      // Seed with the day the team already scheduled, so converting an existing
      // shoot to a hard date is not a blind re-pick.
      const day = scheduledDay || nextStart || null;
      setRange([day, day]);
    } else {
      // Leaving exact: keep the day as the start and force a real end date, so
      // nobody re-saves the same single day under the "range" label.
      setRange([nextStart, null]);
    }
    setPickedMode(next);
  };

  const handleSaveClick = () => {
    if (!nextStart || !nextEnd) {
      toast.error(
        mode === "exact"
          ? "Select the filming date."
          : "Select a start and end date."
      );
      return;
    }
    if (degenerateExit) {
      toast.error("Pick an end date after the start date to go back to a range.");
      return;
    }
    if (daysProvided && !canWriteDays) {
      toast.error(
        mode === "exact"
          ? "Can't set a filming date until the project finishes loading."
          : "Can't clear the filming date until the project finishes loading."
      );
      return;
    }
    if (hasConsequences) {
      setConfirming(true);
      return;
    }
    handleSave();
  };

  const handleSave = async () => {
    const [start, end] = range;
    if (!start || !end) {
      toast.error(
        mode === "exact"
          ? "Select the filming date."
          : "Select a start and end date."
      );
      return;
    }
    if (daysProvided && !canWriteDays) {
      toast.error(
        mode === "exact"
          ? "Can't set a filming date until the project finishes loading."
          : "Can't clear the filming date until the project finishes loading."
      );
      return;
    }
    setConfirming(false);
    setSaving(true);
    try {
      // Range mode saves the WINDOW, exactly as it always has.
      // Date mode writes the DAY through the project endpoint the scheduling
      // dialog already uses -- PATCH /brands/:brandId/projects/:id
      // { film_days, film_dates } -- which validates the day against the
      // window, writes the day rows and the film_date mirror, and asks the
      // opted-in experts to confirm. Sending no `status` keeps its
      // Pre-Production gates (ExpertsNotConfirmed / FilmDaysIncomplete) out of
      // the way, so a date can be set before anyone is even pitched.
      const dayKey = start.format(DAY_KEY);
      const windowUrl = updateSelectionFilmWindow(
        Number(orderId),
        Number(selectionId)
      );
      const patchDays = async (body: Record<string, unknown>) => {
        // Guarded in handleSaveClick and again above; re-asserted here because
        // dropping the write while the flow went on to toast "Filming date set
        // to ..." is the one outcome this must never have.
        if (!canWriteDays) throw new Error("MissingProjectForFilmDate");
        const res = await axiosInstant.patch(
          `/brands/${Number(brandId)}/projects/${Number(projectId)}`,
          body
        );
        return res?.data || null;
      };

      let data: any = null;
      if (mode === "range") {
        // Leaving a set date: give the day back so the experts are asked for a
        // preferred one again. film_days is deliberately NOT sent -- the shoot
        // LENGTH is not this control's to change.
        //
        // Ordered day-write FIRST. These are two requests with no transaction
        // around them, so the order decides what a half-failure leaves behind:
        // clearing the day is legal under ANY window, so a failure here leaves
        // the old window AND the old day -- an untouched, retryable selection.
        // Widening the window first and then failing would strand a hard date
        // the user had just given up inside a range that no longer implies it.
        if (exitingExact) await patchDays({ film_dates: [] });
        ({ data } = await axiosInstant.patch(windowUrl, {
          est_film_start: dayKey,
          est_film_end: end.format(DAY_KEY),
        }));
      } else {
        // The window is touched ONLY when there is none: without one the
        // experts cannot be pitched at all (sendOptInNotifications -> 400
        // FilmWindowRequired). An existing range is left exactly as it is, so
        // the window endpoint's roster voiding never runs here.
        const createdWindow = !hasFilmWindow(filmWindow);
        if (createdWindow) {
          ({ data } = await axiosInstant.patch(windowUrl, {
            est_film_start: dayKey,
            est_film_end: dayKey,
          }));
        }
        try {
          await patchDays({ film_days: 1, film_dates: [dayKey] });
        } catch (dayErr) {
          // The window this save invented exists only to hold the day that
          // just failed to land. Roll it back, or the retry starts from a
          // one-day window that silently constrains every later pick to it.
          if (createdWindow) {
            try {
              await axiosInstant.patch(windowUrl, {
                est_film_start: null,
                est_film_end: null,
              });
            } catch (rollbackErr) {
              console.error("film-window rollback failed:", rollbackErr); // eslint-disable-line no-console
            }
          }
          throw dayErr;
        }
      }
      const saved = data?.process || data || filmWindow;
      // ONLY the window endpoint reports side-effect counters; the project
      // endpoint that writes the day returns the project row. So when this
      // save never touched the window -- exact mode with a range already set,
      // which is the common reschedule -- there is no server number to read
      // and the count falls back to what the dialog predicted. Without this
      // a committed reschedule reported nothing at all.
      const movedBack =
        Number(data?.projects_moved_back) ||
        (movesProjectsBack ? Math.max(projectCount, 1) : 0);
      const preferredReset = Number(data?.preferred_dates_reset) || 0;
      const sideEffects = Boolean(
        movedBack ||
          preferredReset ||
          data?.date_confirmations_reset ||
          data?.film_date_dropped ||
          // The days this save wrote change the roster's question, so the
          // parent has to re-read either way.
          daysProvided
      );
      if (mode === "exact") {
        // Checked FIRST. A hard-date reschedule comes back with
        // film_date_dropped = true (the OLD day was dropped) even though this
        // save set a NEW one, so the generic branch below would announce
        // "Filming date cleared." — the exact opposite of what happened.
        const dayLabel = start.format("MMM D, YYYY");
        if (movedBack > 0) {
          toast.success(
            `Filming date set to ${dayLabel}. ${movedBack} project${
              movedBack === 1 ? "" : "s"
            } moved back to Scheduling.`
          );
        } else {
          toast.success(`Filming date set to ${dayLabel}.`);
        }
      } else if (movedBack > 0) {
        toast.success(
          `Range saved. ${movedBack} project${
            movedBack === 1 ? "" : "s"
          } moved back to Scheduling.`
        );
      } else if (data?.film_date_dropped || exitingExact) {
        toast.success(
          "Range saved. Filming date cleared — experts are asked for a preferred day."
        );
      } else if (preferredReset > 0) {
        toast.success(
          `Range saved. ${preferredReset} expert${
            preferredReset === 1 ? "" : "s"
          } asked for a new preferred date.`
        );
      } else {
        toast.success("Filming range saved.");
      }
      setAnchor(null);
      if (onSaved) onSaved(saved, sideEffects);
    } catch (error: any) {
      // failedError does res.status(code).json(error) with a BARE STRING, so
      // the body is a JSON string, not { error }. Unwrap both shapes before
      // mapping, or every film-day code renders as the generic fallback.
      const body = error?.response?.data;
      const code = typeof body === "string" ? body : body?.error;
      toast.error(
        FILM_DAYS_ERRORS[code] ||
          code ||
          (mode === "exact"
            ? "Couldn't save the filming date."
            : "Couldn't save the filming range.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box
        onClick={interactive ? openPicker : undefined}
        className={`flex items-center gap-2 rounded-lg border border-border-token px-3 py-2 ${
          interactive ? "cursor-pointer hover:bg-surface-2" : ""
        }`}
      >
        <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: "var(--accent)" }} />
        <Box className="flex flex-col">
          <Typography className="!font-circular !text-[10px] !font-bold !uppercase !tracking-wider !text-text-2">
            {savedIsExact ? "Filming Date" : "Filming Range"}
          </Typography>
          <Typography
            className={`!font-circular !text-[13px] !font-[450] ${
              savedDisplay ? "!text-text-1" : "!text-text-2"
            }`}
          >
            {label}
          </Typography>
        </Box>
      </Box>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: "16px", p: 2 } }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Box className="flex flex-col gap-1.5 pb-2">
          {/* The eyebrow names the axis being chosen. Without it the two
              buttons read as a widget switch rather than a decision about what
              the experts get asked. Reuses the pill's own label treatment. */}
          <Typography className="!font-circular !text-[10px] !font-bold !uppercase !tracking-wider !text-text-2">
            What the experts are asked
          </Typography>
          {exactAvailable ? (
            <>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={mode}
                onChange={handleModeChange}
                disabled={saving}
                sx={{
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    px: 1.5,
                    py: 0.25,
                    fontSize: 13,
                  },
                }}
              >
                <ToggleButton value="range">Filming range</ToggleButton>
                <ToggleButton value="exact">Filming date</ToggleButton>
              </ToggleButtonGroup>
              {/* The consequence belongs WITH the decision, not under the
                  calendar. This is the only sentence that distinguishes the
                  two modes, so it sits where the choice is made. */}
              <Typography className="!font-circular !text-[12px] !font-[450] !leading-[1.5] !text-text-2">
                {mode === "exact"
                  ? "They answer yes or no: can they film that day."
                  : "They pick one preferred day inside the range."}
              </Typography>
            </>
          ) : (
            // The option is not hidden silently: a multi-day shoot has its days
            // picked in the scheduling dialog, and saying so is the difference
            // between "missing" and "not here".
            <Typography className="!font-circular !text-[12px] !font-[450] !leading-[1.5] !text-text-2">
              They pick one preferred day inside the range. This is a{" "}
              {filmDaysCount} day shoot, so its days are set in the scheduling
              step.
            </Typography>
          )}
        </Box>
        <ExpertRangeCalendar
          value={range}
          onChange={setRange}
          minDate={exactMinDate}
          maxDate={exactMaxDate}
          singleDate={mode === "exact"}
          caption={calendarCaption}
        />
        <Box className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outlined" size="small" onClick={() => setAnchor(null)}>
            Cancel
          </Button>
          <Button
            size="small"
            disabled={saving || !range[0] || !range[1]}
            onClick={handleSaveClick}
          >
            {saveButtonLabel}
          </Button>
        </Box>
      </Popover>

      <ConfirmationDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={handleSave}
        title={dialogTitle}
        description={changeConsequences}
        confirmLabel={dialogConfirmLabel}
        cancelLabel={
          mode === "exact" ? "Keep current schedule" : "Keep current range"
        }
        disabled={saving}
      />
    </>
  );
};
